import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit3, Trash2, Shield, Mail, Phone, Calendar, Loader, UserCheck, UserX, Save, X, Copy, Search, Key } from 'lucide-react';
import { teamService, TeamMember, CreateTeamMemberRequest, UpdateTeamMemberRequest } from '../services/teamService';
import { useLocalization } from '../contexts/LocalizationContext';

const TeamManagement: React.FC = () => {
  const { t } = useLocalization();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [resettingPassword, setResettingPassword] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [stats, setStats] = useState({
    total_members: 0,
    active_members: 0,
    pending_members: 0,
    roles_stats: { admin: 0, finance: 0, support: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [editingMember, setEditingMember] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [newMember, setNewMember] = useState({
    firstname: '',
    lastname: '',
    email: '',
    niveau: 2,
    password: ''
  });
  const [editForm, setEditForm] = useState<UpdateTeamMemberRequest>({});

  useEffect(() => {
    loadTeamData();
  }, []);

  const loadTeamData = async () => {
    try {
      setLoading(true);
      const [members, teamStats, userData] = await Promise.all([
        teamService.getTeamMembers(),
        teamService.getTeamStats(),
        getCurrentUserData()
      ]);

      setTeamMembers(members);
      setStats(teamStats);
      setCurrentUser(userData);
    } catch (error) {
      console.error('Error loading team data:', error);
      alert(t('team.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const getCurrentUserData = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        return await response.json();
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    }
    return null;
  };

  const getRoleLabel = (niveau: number) => {
    switch (niveau) {
      case 0: return t('role.merchantAdmin');
      case 1: return t('team.financeTeam');
      case 2: return t('team.supportTeam');
      default: return 'User';
    }
  };

  const getRoleColor = (niveau: number) => {
    switch (niveau) {
      case 0: return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      case 1: return 'bg-blue-50 text-blue-700 border border-blue-200';
      case 2: return 'bg-teal-50 text-teal-700 border border-teal-200';
      default: return 'bg-gray-50 text-gray-700 border border-gray-200';
    }
  };

  const getRoleAvatarColor = (niveau: number) => {
    switch (niveau) {
      case 0: return 'bg-emerald-600';
      case 1: return 'bg-blue-600';
      case 2: return 'bg-teal-600';
      default: return 'bg-gray-600';
    }
  };

  const getStatusColor = (status: string, userStatus: string) => {
    if (status === 'Inactive') return 'bg-red-50 text-red-700 border border-red-200';
    if (userStatus === 'pending') return 'bg-amber-50 text-amber-700 border border-amber-200';
    if (userStatus === 'online') return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    return 'bg-gray-50 text-gray-600 border border-gray-200';
  };

  const getStatusLabel = (status: string, userStatus: string) => {
    if (status === 'Inactive') return t('status.inactive');
    if (userStatus === 'pending') return t('team.pending');
    if (userStatus === 'online') return t('status.online');
    if (userStatus === 'offline') return t('status.offline');
    return t('status.active');
  };

  const getOnlineDot = (status: string, userStatus: string) => {
    if (status === 'Inactive') return 'bg-red-500';
    if (userStatus === 'online') return 'bg-emerald-500';
    if (userStatus === 'pending') return 'bg-amber-500';
    return 'bg-gray-400';
  };

  const handleResetPassword = async (member: TeamMember) => {
    try {
      const randomPassword = teamService.generateRandomPassword();
      await teamService.resetUserPassword(member.id, randomPassword);
      setNewPassword(randomPassword);
      setResettingPassword(member.id);
      setShowPasswordModal(true);
      alert(`${t('team.passwordResetSuccess')} ${member.firstname} ${member.lastname}`);
    } catch (error: any) {
      console.error('Error resetting password:', error);
      alert(error.response?.data?.detail || t('team.errorResetPassword'));
    }
  };

  const handleToggleStatus = async (member: TeamMember) => {
    const newStatus = member.status === 'Active' ? 'Inactive' : 'Active';
    const action = newStatus === 'Active' ? 'activate' : 'deactivate';

    if (confirm(`${member.status === 'Active' ? t('team.confirmDeactivate') : t('team.confirmActivate')} ${member.firstname} ${member.lastname}?`)) {
      try {
        await teamService.toggleUserStatus(member.id, newStatus);
        await loadTeamData();
        alert(newStatus === 'Active' ? t('team.userActivated') : t('team.userDeactivated'));
      } catch (error: any) {
        console.error(`Error ${action}ing user:`, error);
        alert(error.response?.data?.detail || t('team.errorLoading'));
      }
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setResettingPassword(null);
    setNewPassword('');
  };

  const copyPasswordToClipboard = () => {
    navigator.clipboard.writeText(newPassword);
    alert(t('team.passwordCopied'));
  };

  const generateUsername = (firstname: string, lastname: string) => {
    const baseUsername = `${firstname.toLowerCase().replace(/\s+/g, '')}.${lastname.toLowerCase().replace(/\s+/g, '')}`;
    let username = baseUsername;
    let counter = 1;
    const usernameExists = (usernameToCheck: string) => {
      return teamMembers.some(member => member.username === usernameToCheck);
    };
    while (usernameExists(username)) {
      username = `${baseUsername}${counter}`;
      counter++;
    }
    return username;
  };

  const handleAddMember = async () => {
    if (!newMember.firstname || !newMember.lastname || !newMember.email || !newMember.password) {
      alert(t('team.fillRequiredFields'));
      return;
    }
    try {
      const generatedUsername = generateUsername(newMember.firstname, newMember.lastname);
      const memberData: CreateTeamMemberRequest = {
        firstname: newMember.firstname,
        lastname: newMember.lastname,
        username: generatedUsername,
        email: newMember.email,
        password: newMember.password,
        merchant_code: currentUser?.merchant_code || '',
        niveau: newMember.niveau
      };
      await teamService.createTeamMember(memberData);
      await loadTeamData();
      setNewMember({ firstname: '', lastname: '', email: '', niveau: 2, password: '' });
      setIsAddingMember(false);
      alert(t('team.memberAdded'));
    } catch (error: any) {
      console.error('Error adding member:', error);
      alert(error.response?.data?.detail || t('team.errorAdding'));
    }
  };

  const startEditMember = (member: TeamMember) => {
    setEditingMember(member.id);
    setEditForm({
      firstname: member.firstname,
      lastname: member.lastname,
      email: member.email,
      niveau: member.niveau,
      status: member.status
    });
  };

  const handleUpdateMember = async (id: string) => {
    try {
      await teamService.updateTeamMember(id, editForm);
      await loadTeamData();
      setEditingMember(null);
      setEditForm({});
      alert(t('team.memberUpdated'));
    } catch (error: any) {
      console.error('Error updating member:', error);
      alert(error.response?.data?.detail || t('team.errorUpdating'));
    }
  };

  const cancelEdit = () => {
    setEditingMember(null);
    setEditForm({});
  };

  const handleDeleteMember = async (id: string, name: string) => {
    if (confirm(`${t('team.confirmDelete')} ${name}?`)) {
      try {
        await teamService.deleteTeamMember(id);
        await loadTeamData();
        alert(t('team.memberDeleted'));
      } catch (error: any) {
        console.error('Error deleting member:', error);
        alert(error.response?.data?.detail || t('team.errorDeleting'));
      }
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return t('team.never');
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredMembers = teamMembers.filter(member => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      member.firstname.toLowerCase().includes(search) ||
      member.lastname.toLowerCase().includes(search) ||
      member.email.toLowerCase().includes(search) ||
      member.username.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-[#63d400] mx-auto" />
          <p className="text-sm text-gray-500 mt-3">{t('team.title')}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">

        {/* Header Section */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-5 sm:p-6 text-white">
          <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)", backgroundSize: "28px 28px" }} />
          <div className="absolute -top-32 -right-20 w-96 h-96 bg-emerald-500/20 rounded-full blur-[100px]" />
          <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-teal-500/15 rounded-full blur-[100px]" />
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-white">{t('team.title')}</h1>
              <p className="text-slate-300 mt-1 text-sm">{t('team.subtitle')}</p>
            </div>
            <div className="mt-4 sm:mt-0">
              <button
                onClick={() => setIsAddingMember(true)}
                className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-[#63d400] to-[#4CAF50] text-white rounded-xl hover:from-[#58c400] hover:to-[#45a049] transition-all duration-200 text-sm font-semibold shadow-md"
              >
                <Plus className="h-5 w-5" />
                {t('team.addMember')}
              </button>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('team.totalMembers')}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total_members}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Users className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('team.activeMembers')}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.active_members}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                <UserCheck className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('team.pending')}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.pending_members}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-amber-50 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('team.adminTeam')}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.roles_stats.admin}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Add Member Form */}
        {isAddingMember && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-[#63d400] to-[#4CAF50] px-6 py-4">
              <div className="flex items-center">
                <Plus className="h-6 w-6 text-white mr-3" />
                <h2 className="text-xl font-bold text-white">{t('team.addNewMember')}</h2>
              </div>
              <p className="text-green-100 text-sm mt-1">{t('team.fillRequiredFields')}</p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t('profile.firstName')} *</label>
                  <input
                    type="text"
                    value={newMember.firstname}
                    onChange={(e) => setNewMember({...newMember, firstname: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#63d400] focus:border-transparent transition-all duration-200"
                    placeholder={t('profile.firstName')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t('profile.lastName')} *</label>
                  <input
                    type="text"
                    value={newMember.lastname}
                    onChange={(e) => setNewMember({...newMember, lastname: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#63d400] focus:border-transparent transition-all duration-200"
                    placeholder={t('profile.lastName')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t('profile.email')} *</label>
                  <input
                    type="email"
                    value={newMember.email}
                    onChange={(e) => setNewMember({...newMember, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#63d400] focus:border-transparent transition-all duration-200"
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t('team.role')}</label>
                  <select
                    value={newMember.niveau}
                    onChange={(e) => setNewMember({...newMember, niveau: parseInt(e.target.value)})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#63d400] focus:border-transparent transition-all duration-200 bg-white"
                  >
                    <option value={1}>{t('team.financeTeam')}</option>
                    <option value={2}>{t('team.supportTeam')}</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t('team.tempPassword')} *</label>
                  <input
                    type="password"
                    value={newMember.password}
                    onChange={(e) => setNewMember({...newMember, password: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#63d400] focus:border-transparent transition-all duration-200"
                    placeholder={t('team.tempPassword')}
                  />
                  <p className="text-xs text-gray-500 mt-2">{t('team.tempPasswordNote')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-6 pt-6 border-t border-gray-100">
                <button
                  onClick={handleAddMember}
                  className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-[#63d400] to-[#4CAF50] text-white rounded-xl hover:from-[#58c400] hover:to-[#45a049] transition-all duration-200 text-sm font-semibold shadow-md"
                >
                  <Plus className="h-4 w-4" />
                  {t('team.addMember')}
                </button>
                <button
                  onClick={() => setIsAddingMember(false)}
                  className="px-5 py-3 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-200"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Team Members Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center">
                <Users className="h-6 w-6 text-white mr-3" />
                <div>
                  <h2 className="text-xl font-bold text-white">{t('team.teamMembers')}</h2>
                  <p className="text-emerald-100 text-sm mt-0.5">{teamMembers.length} {t('team.membersFound')}</p>
                </div>
              </div>
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-200" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('common.search') + '...'}
                  className="w-full pl-10 pr-4 py-2.5 bg-white/20 border border-white/30 rounded-xl text-sm text-white placeholder-emerald-200 focus:ring-2 focus:ring-white/40 focus:border-transparent focus:bg-white/30 transition-all duration-200"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/80">
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('team.member')}</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('team.role')}</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.status')}</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('team.lastLogin')}</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('team.joinedDate')}</th>
                  <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {/* Current User (Admin) */}
                {currentUser && (
                  <tr className="bg-emerald-50/40">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-md">
                            {currentUser.firstname?.charAt(0)}{currentUser.lastname?.charAt(0)}
                          </div>
                          <div className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-emerald-500 border-2 border-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-900">{currentUser.firstname} {currentUser.lastname}</span>
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-sm">{t('team.you')}</span>
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <Mail className="h-3.5 w-3.5 text-gray-400" />
                            <p className="text-xs text-gray-500">{currentUser.email}</p>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {t('role.merchantAdmin')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {t('status.online')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-500">{t('team.now')}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-500">{formatDate(currentUser.created_at)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-400">-</td>
                  </tr>
                )}

                {/* Team members */}
                {filteredMembers.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50/50 transition-colors duration-200">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {editingMember === member.id ? (
                          <div className="flex-1 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <input
                                type="text"
                                value={editForm.firstname || ''}
                                onChange={(e) => setEditForm({...editForm, firstname: e.target.value})}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#63d400] focus:border-transparent transition-all duration-200"
                                placeholder={t('profile.firstName')}
                              />
                              <input
                                type="text"
                                value={editForm.lastname || ''}
                                onChange={(e) => setEditForm({...editForm, lastname: e.target.value})}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#63d400] focus:border-transparent transition-all duration-200"
                                placeholder={t('profile.lastName')}
                              />
                            </div>
                            <input
                              type="email"
                              value={editForm.email || ''}
                              onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#63d400] focus:border-transparent transition-all duration-200"
                              placeholder={t('profile.email')}
                            />
                          </div>
                        ) : (
                          <>
                            <div className="relative">
                              <div className={`w-10 h-10 ${getRoleAvatarColor(member.niveau)} rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-md`}>
                                {member.firstname?.charAt(0)}{member.lastname?.charAt(0)}
                              </div>
                              <div className={`absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full ${getOnlineDot(member.status, member.user_status)} border-2 border-white`} />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-900">{member.firstname} {member.lastname}</p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <Mail className="h-3.5 w-3.5 text-gray-400" />
                                <p className="text-xs text-gray-500">{member.email}</p>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {editingMember === member.id ? (
                        <select
                          value={editForm.niveau || member.niveau}
                          onChange={(e) => setEditForm({...editForm, niveau: parseInt(e.target.value)})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#63d400] focus:border-transparent transition-all duration-200 bg-white"
                        >
                          <option value={1}>{t('team.financeTeam')}</option>
                          <option value={2}>{t('team.supportTeam')}</option>
                        </select>
                      ) : (
                        <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${getRoleColor(member.niveau)}`}>
                          {getRoleLabel(member.niveau)}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {editingMember === member.id ? (
                        <select
                          value={editForm.status || member.status}
                          onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#63d400] focus:border-transparent transition-all duration-200 bg-white"
                        >
                          <option value="Active">{t('status.active')}</option>
                          <option value="Inactive">{t('status.inactive')}</option>
                        </select>
                      ) : (
                        <div className="flex flex-col gap-1.5">
                          <span className={`inline-flex w-fit px-3 py-1 rounded-full text-xs font-semibold ${
                            member.status === 'Active' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
                          }`}>
                            {member.status === 'Active' ? t('status.active') : t('status.inactive')}
                          </span>
                          <span className={`inline-flex w-fit px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(member.status, member.user_status)}`}>
                            {getStatusLabel(member.status, member.user_status)}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-500">
                          {member.last_login ? formatDate(member.last_login) : t('team.never')}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-500">{formatDate(member.created_at)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        {editingMember === member.id ? (
                          <>
                            <button
                              onClick={() => handleUpdateMember(member.id)}
                              className="p-2 text-white bg-gradient-to-r from-[#63d400] to-[#4CAF50] rounded-xl hover:from-[#58c400] hover:to-[#45a049] transition-all duration-200 shadow-sm"
                              title={t('common.save')}
                            >
                              <Save className="h-4 w-4" />
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="p-2 text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200"
                              title={t('common.cancel')}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEditMember(member)}
                              className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all duration-200"
                              title={t('common.edit')}
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleResetPassword(member)}
                              className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-xl transition-all duration-200"
                              title={t('team.resetPassword')}
                            >
                              <Key className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleToggleStatus(member)}
                              className={`p-2 rounded-xl transition-all duration-200 ${
                                member.status === 'Active'
                                  ? 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                                  : 'text-gray-400 hover:text-emerald-600 hover:bg-emerald-50'
                              }`}
                              title={member.status === 'Active' ? t('team.deactivateUser') : t('team.activateUser')}
                            >
                              {member.status === 'Active' ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                            </button>
                            <button
                              onClick={() => handleDeleteMember(member.id, `${member.firstname} ${member.lastname}`)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200"
                              title={t('common.delete')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {teamMembers.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-100 flex items-center justify-center">
                  <Users className="h-7 w-7 text-gray-400" />
                </div>
                <p className="text-sm font-semibold text-gray-900">{t('team.noMembersFound')}</p>
                <p className="text-xs text-gray-500 mt-1">{t('team.startAddMember')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Role Permissions Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
            <div className="flex items-center">
              <Shield className="h-6 w-6 text-white mr-3" />
              <h2 className="text-xl font-bold text-white">{t('team.permissionsByRole')}</h2>
            </div>
            <p className="text-emerald-100 text-sm mt-1">{t('team.permissionsByRole')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-emerald-600" />
                </div>
                <h4 className="text-sm font-bold text-gray-900">{t('role.merchantAdmin')}</h4>
              </div>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex items-center gap-2.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{t('team.fullAccess')}</li>
                <li className="flex items-center gap-2.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{t('team.teamManagement')}</li>
                <li className="flex items-center gap-2.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{t('team.accountConfig')}</li>
                <li className="flex items-center gap-2.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{t('team.allPermissions')}</li>
              </ul>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <h4 className="text-sm font-bold text-gray-900">{t('team.financeTeam')}</h4>
              </div>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex items-center gap-2.5"><span className="h-1.5 w-1.5 rounded-full bg-blue-500" />{t('team.viewTransactions')}</li>
                <li className="flex items-center gap-2.5"><span className="h-1.5 w-1.5 rounded-full bg-blue-500" />{t('team.checkBalances')}</li>
                <li className="flex items-center gap-2.5"><span className="h-1.5 w-1.5 rounded-full bg-blue-500" />{t('team.generateReports')}</li>
                <li className="flex items-center gap-2.5"><span className="h-1.5 w-1.5 rounded-full bg-blue-500" />{t('team.financialAnalytics')}</li>
                <li className="flex items-center gap-2.5"><span className="h-1.5 w-1.5 rounded-full bg-blue-500" />{t('team.manageTransfers')}</li>
              </ul>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-xl bg-teal-50 flex items-center justify-center">
                  <Users className="h-5 w-5 text-teal-600" />
                </div>
                <h4 className="text-sm font-bold text-gray-900">{t('team.supportTeam')}</h4>
              </div>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex items-center gap-2.5"><span className="h-1.5 w-1.5 rounded-full bg-teal-500" />{t('team.viewTransactions')}</li>
                <li className="flex items-center gap-2.5"><span className="h-1.5 w-1.5 rounded-full bg-teal-500" />{t('team.customerSupport')}</li>
                <li className="flex items-center gap-2.5"><span className="h-1.5 w-1.5 rounded-full bg-teal-500" />{t('team.checkHistory')}</li>
                <li className="flex items-center gap-2.5"><span className="h-1.5 w-1.5 rounded-full bg-teal-500" />{t('team.basicReporting')}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Password Reset Modal */}
        {showPasswordModal && resettingPassword && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-100">
              <div className="bg-gradient-to-r from-slate-950 to-slate-800 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Key className="h-6 w-6 text-white mr-3" />
                    <h2 className="text-lg font-bold text-white">{t('team.passwordReset')}</h2>
                  </div>
                  <button
                    onClick={closePasswordModal}
                    className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-5">
                  <div className="flex items-center gap-2.5 mb-2">
                    <Key className="h-5 w-5 text-amber-600" />
                    <span className="text-sm font-semibold text-amber-800">{t('team.newPasswordGenerated')}</span>
                  </div>
                  <p className="text-xs text-amber-700 mb-4">{t('team.passwordNote')}</p>

                  <div className="bg-white border border-amber-200 rounded-xl p-4 flex items-center justify-between">
                    <code className="text-sm font-mono text-gray-900 select-all font-semibold">{newPassword}</code>
                    <button
                      onClick={copyPasswordToClipboard}
                      className="ml-3 p-2 text-gray-400 hover:text-[#63d400] hover:bg-green-50 rounded-xl transition-all duration-200 border border-gray-200"
                      title="Copy"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-3">
                <button
                  onClick={copyPasswordToClipboard}
                  className="px-4 py-2.5 text-sm font-semibold text-slate-900 bg-white border border-emerald-200 rounded-xl hover:bg-emerald-50 transition-all duration-200"
                >
                  {t('team.copyPassword')}
                </button>
                <button
                  onClick={closePasswordModal}
                  className="px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl hover:from-slate-800 hover:to-slate-900 transition-all duration-200"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        )}

    </div>
  );
};

export default TeamManagement;
